import React, { useEffect, useMemo, useState } from "react";
import { useAuth } from "react-oidc-context";
import GenerateReportForm from "./GenerateReportForm";
import ReportHistorySection from "./ReportHistorySection";
import { RECORDS_PER_PAGE } from "./constants";
import {
  requestGenerateReport,
  requestHistoryDownload,
  requestReportHistory,
} from "./reportApi";
import {
  filterReportsByCampaignId,
  paginateReports,
  triggerDownload,
} from "./reportUtils";

const CampaignReport = () => {
  const [campaignId, setCampaignId] = useState("");
  const [status, setStatus] = useState({ type: "", message: "" });
  const [reportHistory, setReportHistory] = useState([]);
  const [isGenerating, setIsGenerating] = useState(false);
  const [activeDownloadIndex, setActiveDownloadIndex] = useState(null);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [currentPage, setCurrentPage] = useState(1);

  const auth = useAuth();
  const accessToken = auth.user?.id_token;

  const filteredReports = useMemo(
    () => filterReportsByCampaignId(reportHistory, searchQuery),
    [reportHistory, searchQuery]
  );

  const { indexOfFirstRecord, currentRecords, totalPages } = useMemo(
    () => paginateReports(filteredReports, currentPage, RECORDS_PER_PAGE),
    [filteredReports, currentPage]
  );

  useEffect(() => {
    fetchReportHistory();
  }, []);

  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery, reportHistory]);

  const fetchReportHistory = async () => {
    try {
      setIsRefreshing(true);
      const data = await requestReportHistory({ accessToken });
      setReportHistory(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error("Error fetching history:", err);
      setReportHistory([]);
      setStatus({ type: "error", message: err.message || "Failed to refresh report history." });
    } finally {
      setIsRefreshing(false);
    }
  };

  const handleGenerate = async () => {
    if (isGenerating) return;

    setStatus({ type: "", message: "" });

    if (!campaignId.trim()) {
      setStatus({ type: "error", message: "Please enter a campaign ID." });
      return;
    }

    setIsGenerating(true);

    try {
      const data = await requestGenerateReport({ campaignId, accessToken });

      if (data.download_url) {
        triggerDownload(data.download_url);
        setStatus({ type: "success", message: "Report download started.." });
      } else {
        setStatus({ type: "success", message: data.message });
      }

      fetchReportHistory();
    } catch (err) {
      setStatus({ type: "error", message: err.message });
    } finally {
      setIsGenerating(false);
    }
  };

  const downloadFromHistory = async (item, index) => {
    if (activeDownloadIndex === index) return;

    setStatus({ type: "", message: "" });
    setActiveDownloadIndex(index);

    try {
      const result = await requestHistoryDownload({ item, accessToken });

      if (result.download_url) {
        triggerDownload(result.download_url);
        setStatus({ type: "success", message: "Report downloaded successfully." });
      } else {
        throw new Error("No URL returned");
      }
    } catch (err) {
      setStatus({ type: "error", message: err.message });
    } finally {
      setActiveDownloadIndex(null);
    }
  };

  const handleSearch = (query) => {
    setSearchQuery(query);
  };

  return (
    <div className="w-100 py-1">
      <div className="card shadow-sm w-100">
        <div className="card-body p-4">
          <GenerateReportForm
            campaignId={campaignId}
            setCampaignId={setCampaignId}
            isGenerating={isGenerating}
            onGenerate={handleGenerate}
            status={status}
          />

          <ReportHistorySection
            searchQuery={searchQuery}
            onSearch={handleSearch}
            isRefreshing={isRefreshing}
            onRefresh={fetchReportHistory}
            currentRecords={currentRecords}
            filteredReports={filteredReports}
            indexOfFirstRecord={indexOfFirstRecord}
            activeDownloadIndex={activeDownloadIndex}
            onDownload={downloadFromHistory}
            currentPage={currentPage}
            totalPages={totalPages}
            recordsPerPage={RECORDS_PER_PAGE}
            onPreviousPage={() => setCurrentPage((prev) => prev - 1)}
            onNextPage={() => setCurrentPage((prev) => prev + 1)}
          />
        </div>
      </div>
    </div>
  );
};

export default CampaignReport;
